import { useTheme } from '@mui/material/styles';
import { useRef, type CSSProperties } from 'react';
import { ARIDE_LOADING_VIEW_BOX, buildArideLoadingMarkup } from './arideLoadingMarkup';

/**
 * Cores do loading: as da ilustração estática (`undraw_on-the-way`) no claro, com traço e cenário
 * mais claros no escuro. O fundo vem do tema (`background.default`), então o cenário se mistura à tela.
 */
export const LOADING_COLORS = {
  light: { primary: '#6C63FF', ink: '#3F3D56', accent: '#E4E1EF' },
  dark: { primary: '#6C63FF', ink: '#8E8AA8', accent: '#34323F' }
} as const;

type JourneyQuoteLoadingIllustrationProps = {
  /** Largura em px ou unidade CSS; a altura segue a proporção 18:7. */
  width?: number | string;
};

let instanceCount = 0;

/**
 * Loading animado da jornada: entregadora de scooter em loop de 6 s com cidade em parallax.
 * A animação é só CSS dentro do SVG e para o cenário com `prefers-reduced-motion`.
 * Decorativo: o texto do loading fica no `JourneyLoadingScreen`.
 */
export default function JourneyQuoteLoadingIllustration({ width = '100%' }: JourneyQuoteLoadingIllustrationProps) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  // React 17 não tem useId: um contador por instância evita ids repetidos na máscara entre loadings.
  const id = useRef<string>();
  if (!id.current) {
    instanceCount += 1;
    id.current = `aride-loading-${instanceCount}`;
  }
  const colors = LOADING_COLORS[mode];
  const vars = {
    '--aride-accent': colors.accent,
    '--aride-bg': theme.palette.background.default,
    '--aride-ink': colors.ink,
    '--aride-primary': colors.primary
  } as CSSProperties;

  return (
    <svg
      aria-hidden
      className="aride-loader"
      dangerouslySetInnerHTML={{ __html: buildArideLoadingMarkup(id.current) }}
      data-theme={mode}
      style={{ display: 'block', height: 'auto', ...vars }}
      viewBox={ARIDE_LOADING_VIEW_BOX}
      width={width}
      xmlns="http://www.w3.org/2000/svg"
    />
  );
}
